import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';

/**
 * Hook that wraps any PDF download function with loading/error state and toast notifications.
 *
 * Usage:
 *   const { exporting, exportPdf } = usePdfExport();
 *   <Button onClick={() => exportPdf(() => downloadExamPdf(id, title))} disabled={exporting}>
 *
 * @returns {{ exporting: boolean, error: string|null, exportPdf: (fn: () => Promise<void>) => Promise<void> }}
 */
export default function usePdfExport() {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const exportPdf = useCallback(async (downloadFn) => {
    setExporting(true);
    setError(null);
    try {
      await downloadFn();
      toast.success('PDF downloaded successfully');
    } catch (err) {
      const message = err.message || 'Failed to export PDF';
      setError(message);
      toast.error(message);
    } finally {
      setExporting(false);
    }
  }, []);

  return { exporting, error, exportPdf };
}
